import { useMemo } from 'react';

import { alpha, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  LogarithmicScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { convertValue, formatPercentageValue } from 'src/utils/number';

import type { ChartData, ChartOptions, Plugin, TooltipItem } from 'chart.js';

ChartJS.register(
  Filler,
  Legend,
  LineElement,
  LinearScale,
  LogarithmicScale,
  PointElement,
  Tooltip,
);

export const CONCENTRATION_MARKER_RANKS = [1, 5, 10, 25] as const;

export interface FicFidcConcentrationCurvePoint {
  rank: number;
  accumulatedValue: number;
  accumulatedPercentage: number;
}

interface ConcentrationMarkerPluginOptions {
  markerColor: string;
  textColor: string;
  fontFamily: string | undefined;
}

type ConcentrationMarkerTextAlign = 'left' | 'center' | 'right';

interface ConcentrationMarkerLabelPosition {
  textAlign: ConcentrationMarkerTextAlign;
  x: number;
}

export function getConcentrationMarkerLabelPosition(
  x: number,
  labelWidth: number,
  chartArea: { left: number; right: number },
): ConcentrationMarkerLabelPosition {
  const halfLabelWidth = labelWidth / 2;

  if (x - halfLabelWidth < chartArea.left) {
    return { textAlign: 'left', x: chartArea.left };
  }

  if (x + halfLabelWidth > chartArea.right) {
    return { textAlign: 'right', x: chartArea.right };
  }

  return { textAlign: 'center', x };
}

function formatCurrency(value: number) {
  return convertValue(value, '', true, 'currency', 'BRL', 1, 0);
}

function formatRank(value: string | number) {
  const rank = Number(value);

  return Number.isFinite(rank) ? String(Math.round(rank)) : '';
}

function isHighlightedRank(rank: number) {
  return (CONCENTRATION_MARKER_RANKS as readonly number[]).includes(rank);
}

function getAxisRanks(totalFunds: number) {
  return Array.from(
    new Set(
      [...CONCENTRATION_MARKER_RANKS, totalFunds]
        .filter((rank) => rank <= totalFunds)
        .sort((first, second) => first - second),
    ),
  );
}

function createConcentrationMarkerPlugin(
  points: FicFidcConcentrationCurvePoint[],
  { markerColor, textColor, fontFamily }: ConcentrationMarkerPluginOptions,
): Plugin<'line'> {
  return {
    id: 'fic-fidc-concentration-markers',
    afterDatasetsDraw: (chart) => {
      const datasetMeta = chart.getDatasetMeta(0);
      const { ctx, chartArea } = chart;

      CONCENTRATION_MARKER_RANKS.forEach((rank) => {
        const point = points[rank - 1];
        const element = datasetMeta.data[rank - 1] as
          | { x: number; y: number }
          | undefined;

        if (!point || !element) return;

        ctx.save();
        ctx.strokeStyle = markerColor;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(element.x, chartArea.bottom);
        ctx.lineTo(element.x, element.y);
        ctx.stroke();

        ctx.fillStyle = textColor;
        ctx.font = '600 12px ' + fontFamily;
        ctx.textBaseline = 'bottom';
        ctx.setLineDash([]);
        const label = `Top ${rank}: ${formatPercentageValue(point.accumulatedPercentage, 1)}`;
        const labelPosition = getConcentrationMarkerLabelPosition(
          element.x,
          ctx.measureText(label).width,
          chartArea,
        );
        ctx.textAlign = labelPosition.textAlign;
        ctx.fillText(
          label,
          labelPosition.x,
          Math.max(chartArea.top + 16, element.y - 10),
        );
        ctx.restore();
      });
    },
  };
}

interface FicFidcConcentrationCurveProps {
  points: FicFidcConcentrationCurvePoint[];
  totalFunds: number;
  height?: number | Partial<Record<'xs' | 'sm' | 'md', number>>;
  marginTop?: number;
  testId?: string;
}

export default function FicFidcConcentrationCurve({
  points,
  totalFunds,
  height = { xs: 320, sm: 360, md: 420 },
  marginTop = 2,
  testId = 'fic-fidc-concentration-curve',
}: FicFidcConcentrationCurveProps) {
  const theme = useTheme();
  const axisRanks = useMemo(() => getAxisRanks(totalFunds), [totalFunds]);
  const data = useMemo<ChartData<'line'>>(
    () => ({
      datasets: [
        {
          label: 'Concentração acumulada',
          data: points.map((point) => ({
            x: point.rank,
            y: point.accumulatedPercentage,
          })),
          backgroundColor: alpha(theme.palette.primary.main, 0.12),
          borderWidth: 2,
          borderColor: theme.palette.primary.main,
          fill: true,
          pointBackgroundColor: points.map(() => theme.palette.primary.main),
          pointBorderColor: points.map(() => theme.palette.background.paper),
          pointBorderWidth: 2,
          pointHitRadius: 8,
          pointHoverRadius: points.map((point) =>
            isHighlightedRank(point.rank) ? 7 : 4,
          ),
          pointRadius: points.map((point) =>
            isHighlightedRank(point.rank) ? 5 : 0,
          ),
          tension: 0.16,
        },
      ],
    }),
    [points, theme.palette.background.paper, theme.palette.primary.main],
  );

  const markerPlugin = useMemo(
    () =>
      createConcentrationMarkerPlugin(points, {
        fontFamily: theme.typography.fontFamily,
        markerColor: alpha(theme.palette.primary.main, 0.45),
        textColor: theme.palette.text.primary,
      }),
    [
      points,
      theme.palette.primary.main,
      theme.palette.text.primary,
      theme.typography.fontFamily,
    ],
  );

  const options = useMemo<ChartOptions<'line'>>(
    () => ({
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      events: ['mousemove', 'mouseout', 'touchstart', 'touchmove'],
      interaction: {
        intersect: false,
        mode: 'nearest',
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: theme.palette.secondary.main,
          bodyColor: theme.palette.primary.contrastText,
          borderColor: alpha(theme.palette.primary.main, 0.5),
          borderWidth: 1,
          callbacks: {
            title: (items: TooltipItem<'line'>[]) => {
              const dataIndex = items[0]?.dataIndex;

              return dataIndex === undefined
                ? ''
                : 'Top ' + (points[dataIndex]?.rank ?? '');
            },
            label: (item: TooltipItem<'line'>) => {
              const point = points[item.dataIndex];

              if (!point) return '';

              return [
                'Percentual acumulado: ' +
                  formatPercentageValue(point.accumulatedPercentage, 1),
                'Valor financeiro acumulado: ' +
                  formatCurrency(point.accumulatedValue),
              ];
            },
          },
          displayColors: false,
          padding: 10,
          titleColor: theme.palette.primary.contrastText,
        },
      },
      layout: { padding: { top: 26, right: 8 } },
      scales: {
        x: {
          type: 'logarithmic',
          min: 1,
          max: totalFunds > 1 ? totalFunds : undefined,
          afterBuildTicks: (scale) => {
            scale.ticks = axisRanks.map((value) => ({ value }));
          },
          ticks: {
            callback: (value: string | number) => formatRank(value),
            color: theme.palette.text.secondary,
            padding: 6,
          },
          grid: {
            color: theme.palette.divider,
            drawOnChartArea: false,
          },
        },
        y: {
          type: 'linear',
          min: 0,
          max: 100,
          ticks: {
            callback: (value: string | number) =>
              String(Number(value).toFixed(0)) + '%',
            color: theme.palette.text.secondary,
            stepSize: 20,
          },
          grid: { color: theme.palette.divider },
        },
      },
    }),
    [
      axisRanks,
      points,
      theme.palette.divider,
      theme.palette.primary.contrastText,
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.text.secondary,
      totalFunds,
    ],
  );

  return (
    <Box
      aria-label="Curva de concentração da carteira"
      data-testid={testId}
      role="img"
      sx={{ height, mt: marginTop }}>
      <Line data={data} options={options} plugins={[markerPlugin]} />
    </Box>
  );
}

export type { FicFidcConcentrationCurveProps };
